import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import toast from 'react-hot-toast';
import LoadingScreen from '../components/LoadingScreen';

interface ProfileData {
  id: number;
  username: string;
  email: string;
  name: string;
  phoneNumber?: string;
  isActive: boolean;
  emailVerified: boolean;
  roles: string[];
  vendorId?: number;
  createdAt: string;
  updatedAt: string;
  // Steward-specific fields
  stewardCode?: string;
  isOnDuty?: boolean;
  lastDutyStart?: string;
  lastDutyEnd?: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, signout } = useAuth();
  const { theme } = useTheme();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: ''
  });

  // Fetch profile on mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    fetchProfile();
  }, [isAuthenticated, navigate]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/profile/me');
      const profileData = response.data;

      setProfile(profileData);
      setFormData({
        name: profileData.name || '',
        email: profileData.email || '',
        phoneNumber: profileData.phoneNumber || ''
      });
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      toast.error(error.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      const response = await api.put('/api/profile/me', formData);

      setProfile(response.data.profile);
      setEditing(false);
      toast.success(response.data.message || 'Profile updated successfully');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || ''
      });
    }
    setEditing(false);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ROLE_ADMIN':
        return { bg: 'rgba(239, 68, 68, 0.1)', text: '#D2453C', border: 'rgba(239, 68, 68, 0.3)' };
      case 'ROLE_VENDOR':
        return { bg: 'rgba(59, 130, 246, 0.1)', text: '#2D6CB5', border: 'rgba(59, 130, 246, 0.3)' };
      case 'ROLE_STEWARD':
        return { bg: 'rgba(168, 85, 247, 0.1)', text: '#6D5BD0', border: 'rgba(168, 85, 247, 0.3)' };
      case 'ROLE_USER':
        return { bg: 'rgba(34, 197, 94, 0.1)', text: '#1E8E5A', border: 'rgba(34, 197, 94, 0.3)' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.1)', text: '#6b7280', border: 'rgba(107, 114, 128, 0.3)' };
    }
  };

  const formatRoleName = (role: string) => {
    return role.replace('ROLE_', '').charAt(0).toUpperCase() +
           role.replace('ROLE_', '').slice(1).toLowerCase();
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.background }}>
        <div className="text-center">
          <p className="text-lg" style={{ color: theme.text }}>Failed to load profile</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 rounded-lg"
            style={{ backgroundColor: theme.primary, color: 'white' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.background }}>
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-lg border-b" style={{
        backgroundColor: theme.card + 'ee',
        borderColor: theme.border
      }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-lg hover:bg-opacity-10"
                style={{ color: theme.text }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>
                My Profile
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-lg p-6 shadow-sm" style={{ backgroundColor: theme.card }}>
          {/* Profile Header */}
          <div className="flex items-center justify-between mb-6 pb-6 border-b" style={{ borderColor: theme.border }}>
            <div className="flex items-center space-x-4">
              {/* Avatar */}
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold"
                style={{
                  backgroundColor: theme.primary + '20',
                  color: theme.primary
                }}
              >
                {profile.name?.charAt(0).toUpperCase() || profile.username?.charAt(0).toUpperCase()}
              </div>

              <div>
                <h2 className="text-2xl font-bold" style={{ color: theme.text }}>
                  {profile.name || profile.username}
                </h2>
                <p className="text-sm" style={{ color: theme.gray[500] }}>
                  @{profile.username}
                </p>

                {/* Roles */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {profile.roles.map((role) => {
                    const colors = getRoleBadgeColor(role);
                    return (
                      <span
                        key={role}
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.text,
                          border: `1px solid ${colors.border}`
                        }}
                      >
                        {formatRoleName(role)}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: theme.primary,
                  color: 'white'
                }}
              >
                Edit Profile
              </button>
            )}
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                  Full Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{
                      backgroundColor: theme.background,
                      borderColor: theme.border,
                      color: theme.text
                    }}
                    placeholder="Enter your full name"
                  />
                ) : (
                  <p className="px-4 py-2 rounded-lg" style={{
                    backgroundColor: theme.background,
                    color: theme.text
                  }}>
                    {profile.name || 'Not set'}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                  Email Address
                  {profile.emailVerified && (
                    <span className="ml-2 text-xs text-green-600">✓ Verified</span>
                  )}
                  {!profile.emailVerified && profile.email && (
                    <span className="ml-2 text-xs text-orange-600">⚠ Not Verified</span>
                  )}
                </label>
                {editing ? (
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{
                      backgroundColor: theme.background,
                      borderColor: theme.border,
                      color: theme.text
                    }}
                    placeholder="Enter your email"
                  />
                ) : (
                  <p className="px-4 py-2 rounded-lg" style={{
                    backgroundColor: theme.background,
                    color: theme.text
                  }}>
                    {profile.email || 'Not set'}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                  Phone Number
                </label>
                {editing ? (
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{
                      backgroundColor: theme.background,
                      borderColor: theme.border,
                      color: theme.text
                    }}
                    placeholder="Enter your phone number"
                  />
                ) : (
                  <p className="px-4 py-2 rounded-lg" style={{
                    backgroundColor: theme.background,
                    color: theme.text
                  }}>
                    {profile.phoneNumber || 'Not set'}
                  </p>
                )}
              </div>

              {/* Username (Read-only) */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.text }}>
                  Username (Cannot be changed)
                </label>
                <p className="px-4 py-2 rounded-lg opacity-60" style={{
                  backgroundColor: theme.background,
                  color: theme.text
                }}>
                  {profile.username}
                </p>
              </div>

              {/* Steward-specific Info */}
              {profile.stewardCode && (
                <div className="pt-4 border-t" style={{ borderColor: theme.border }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>
                    Steward Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.gray[600] }}>
                        Steward Code
                      </label>
                      <p className="font-mono" style={{ color: theme.text }}>
                        {profile.stewardCode}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: theme.gray[600] }}>
                        Status
                      </label>
                      <p style={{ color: profile.isOnDuty ? theme.success : theme.gray[500] }}>
                        {profile.isOnDuty ? '🟢 On Duty' : '⚪ Off Duty'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Info */}
              <div className="pt-4 border-t" style={{ borderColor: theme.border }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>
                  Account Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: theme.gray[600] }}>
                      Account Status
                    </label>
                    <p style={{ color: profile.isActive ? (theme.success || '#16A34A') : (theme.danger || '#DC2626') }}>
                      {profile.isActive ? '✓ Active' : '✗ Inactive'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: theme.gray[600] }}>
                      Member Since
                    </label>
                    <p style={{ color: theme.text }}>
                      {new Date(profile.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {editing && (
                <div className="flex items-center justify-end space-x-4 pt-6 border-t" style={{ borderColor: theme.border }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 rounded-lg font-medium border"
                    style={{
                      borderColor: theme.border,
                      color: theme.text
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 rounded-lg font-medium"
                    style={{
                      backgroundColor: theme.primary,
                      color: 'white'
                    }}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </form>

          {/* Logout */}
          <div className="pt-6 border-t mt-6" style={{ borderColor: theme.border }}>
            <button
              onClick={() => { signout(); navigate('/login'); }}
              className="w-full py-3 rounded-lg font-semibold"
              style={{
                backgroundColor: theme.button?.danger?.bg || '#FEE2E2',
                color: theme.button?.danger?.text || '#DC2626',
                border: `1px solid ${theme.danger || '#DC2626'}40`,
                cursor: 'pointer',
                fontSize: '0.938rem',
                minHeight: 48,
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
